import { useCallback, useMemo, useState } from 'react';
import { QcDiagnosticWorkupDto, QcVisitStateDto } from '../../../services/qualityCheck';
import {
    toReviewImages,
    toAssessmentMap,
    toLesionValueMap,
    toCorrectionsMap,
} from './mapQcVisitState';
import { ReviewImage } from './imageMeta';
import { ImageAssessmentValue } from './ImageAssessment';
import { LesionFeedbackValue } from './LesionPanel';
import { useSerializedKeyedWrites } from './useSerializedKeyedWrites';
import { CorrectionFlagView } from './correctionFlag';

/**
 * Stable empties, so a null visit does not hand the memos a fresh array each render and
 * re-derive everything — including the decrypt targets, which would refetch every image.
 */
const NO_IMAGES: QcVisitStateDto['images'] = [];
const NO_LESIONS: QcVisitStateDto['lesions'] = [];

/**
 * What a loaded Quality Check visit means, in one place.
 *
 * The container used to hold the payload AND four projections of it as peer state, so every
 * path that received a read had to decide which subset to refresh — a question the structure
 * forced and did not help answer. One path answered it wrong and reverted a reviewer's mark,
 * moving the progress that gates Approve; it survived three review gates (SKIN-280).
 *
 * Here the payload is the only server-owned state, and there are exactly two rules on top of
 * it.
 *
 * **Overrides** are what the reviewer changed and the server has not confirmed: the
 * assessment and lesion maps, applied at enqueue time ahead of the network and read as
 * `override ?? server ?? empty`. They are optimistic, and they have a drop rule — a failed
 * write removes the override and the derivation answers with the server's value.
 *
 * **Folds** are facts the server has already accepted that no re-read has mentioned yet: an
 * approved visit, a corrected identifiable flag, an acknowledged assessment. They go straight
 * into the payload, because that is where the server's facts live.
 *
 * How long a fold survives differs by kind, and saying "the next read supersedes them" would
 * be false for one of them. The identifiable flag and the visit state are replaced by the next
 * correction re-read. Assessments are **not**: `applyCorrection` pins every `entry` from the
 * state it already had, because that read can predate a write the server took and taking its
 * `entry` would drop a mark. The consequence, stated rather than glossed: an assessment
 * another reviewer changes on the server does not reach this screen until it is remounted.
 * That is how the screen behaved before this change too — the old correction path never
 * refreshed assessments either — so it is preserved behaviour, not a new limit.
 *
 * The distinction is the whole design. A third shape — a layer that is neither optimistic nor
 * droppable — is shadow state, and it is what this ticket exists to delete.
 */
export function useQcVisitState() {
    const [visit, setVisit] = useState<QcVisitStateDto | null>(null);
    const [assessmentOverrides, setAssessmentOverrides] = useState<Record<string, ImageAssessmentValue>>({});
    const [lesionOverrides, setLesionOverrides] = useState<Record<string, LesionFeedbackValue>>({});
    /**
     * An acknowledged assessment write, recorded where the server's facts live.
     *
     * The same shape as `applyWrittenFlag` and for the same reason: by the time this runs the
     * value has been accepted, so it is not the reviewer's optimistic edit any more. The write
     * hook drops its override immediately after calling this, which is what keeps the value in
     * one place rather than two.
     */
    const foldWrittenAssessment = useCallback((encryptedFileUuid: string, value: ImageAssessmentValue) => {
        setVisit((prev) => (prev ? {
            ...prev,
            images: prev.images.map((image) => (image.encryptedFileUuid === encryptedFileUuid
                ? { ...image, entry: { outcome: value.outcome, categoryIds: value.categoryIds, comment: value.comment } }
                : image)),
        } : prev));
    }, []);

    /** The lesion twin of the fold above. */
    const foldWrittenLesion = useCallback((groupRecordUuid: string, value: LesionFeedbackValue) => {
        setVisit((prev) => (prev ? {
            ...prev,
            lesions: prev.lesions.map((lesion) => (lesion.groupRecordUuid === groupRecordUuid
                ? { ...lesion, entry: { categoryIds: value.categoryIds, comment: value.comment } }
                : lesion)),
        } : prev));
    }, []);

    // One writer per map. Both serialize and coalesce per key.
    const assessmentWrites = useSerializedKeyedWrites(setAssessmentOverrides, foldWrittenAssessment);
    const lesionWrites = useSerializedKeyedWrites(setLesionOverrides, foldWrittenLesion);

    const serverImages = visit?.images ?? NO_IMAGES;
    const serverLesions = visit?.lesions ?? NO_LESIONS;

    const baseImages: ReviewImage[] = useMemo(
        () => toReviewImages(serverImages, serverLesions),
        [serverImages, serverLesions],
    );
    const corrections = useMemo(() => toCorrectionsMap(serverImages), [serverImages]);
    const assessments = useMemo(
        () => ({ ...toAssessmentMap(serverImages), ...assessmentOverrides }),
        [serverImages, assessmentOverrides],
    );
    /**
     * SKIN-441 — which lesion instances the eCRF left without a diagnosis, keyed by the
     * anchor the API addresses lesions with.
     *
     * A set of the UNDIAGNOSED rather than a map of every lesion's verdict, so a section
     * whose uuid the payload does not mention renders unmarked. Marking on absence would
     * cry wolf on the first lesion the two sides disagree about; failing to mark is the
     * quieter, recoverable error — and the served `lesionCounts` below is what states
     * the true total either way.
     */
    const undiagnosedLesions = useMemo(
        () => new Set(serverLesions.filter(l => !l.diagnosed).map(l => l.groupRecordUuid)),
        [serverLesions],
    );
    /**
     * SKIN-611 — each lesion's own diagnostic workup, keyed by the anchor the API
     * addresses lesions with. Only lesions that answered some of it are present.
     */
    const workupByLesion = useMemo(
        () => new Map(serverLesions
            .filter(l => l.diagnosticWorkup)
            .map(l => [l.groupRecordUuid, l.diagnosticWorkup as QcDiagnosticWorkupDto])),
        [serverLesions],
    );
    // SKIN-441 — served, not derived. `serverLesions` is deliberately wider than the
    // counted population (see the API's QcVisitState), so counting it here would put
    // this screen at odds with the queue row that opened it.
    // `null` while nothing is loaded, never `{0, 0}`. Defaulting would state "no
    // lesions, none undiagnosed" — the same confidently-wrong answer the service layer
    // throws to avoid — and would make "not loaded" indistinguishable from "nothing to
    // flag" at every future call site. The field names are the API's, unchanged: a
    // rename here would buy nothing and cost the reader a translation step.
    const lesionCounts = useMemo(
        () => (visit
            ? { lesionCount: visit.lesionCount, undiagnosedLesionCount: visit.undiagnosedLesionCount }
            : null),
        [visit],
    );
    const lesionValues = useMemo(
        () => ({ ...toLesionValueMap(serverLesions), ...lesionOverrides }),
        [serverLesions, lesionOverrides],
    );

    /** The visit read, whole. */
    const applyVisit = useCallback((payload: QcVisitStateDto) => setVisit(payload), []);

    /**
     * A correction's re-read: the fresh payload wins on everything the server owns, and only
     * what this screen owns is pinned from the previous one.
     *
     * The pinning is necessary because the two writes race — this read can answer from before
     * an assessment write landed, and taking its `entry` would drop a mark the server had
     * already accepted, with the override that backed it already released. The reviewer would
     * watch a saved verdict vanish and Approve close behind it.
     *
     * But the pin is stated the other way round on purpose. Listing the fields to *refresh*
     * means every field added to the DTO later is silently stale here, and an earlier version
     * of this copied two of nine and dropped server-side image additions and removals on the
     * floor. The reviewer-owned set is short, closed and type-checked — `entry` and `state` —
     * so pinning that and taking the rest wholesale cannot rot.
     *
     * `state` is pinned for a different reason: refreshing it would flip the screen to
     * read-only mid-correction if someone approved the visit elsewhere, which is a separate
     * feature and a jarring one to meet with a dialog open.
     */
    const applyCorrection = useCallback((payload: QcVisitStateDto) => {
        setVisit((prev) => {
            if (!prev) return payload;
            const priorImages = new Map(prev.images.map((image) => [image.encryptedFileUuid, image]));
            const priorLesions = new Map(prev.lesions.map((lesion) => [lesion.groupRecordUuid, lesion]));
            return {
                ...payload,
                state: prev.state,
                images: payload.images.map((image) => {
                    const prior = priorImages.get(image.encryptedFileUuid);
                    return prior ? { ...image, entry: prior.entry } : image;
                }),
                lesions: payload.lesions.map((lesion) => {
                    const prior = priorLesions.get(lesion.groupRecordUuid);
                    return prior ? { ...lesion, entry: prior.entry } : lesion;
                }),
            };
        });
    }, []);

    /**
     * The flag the server has just accepted, recorded without waiting for the re-read.
     *
     * A fold into the payload, not an override: this value is a server fact by the time it
     * arrives — the correction hook only calls it after the write resolved — so it belongs
     * where the server's facts live, and the next correction re-read does supersede it. An
     * override layer here would have no drop rule and would mask the payload for the life of
     * the mount, which is the shadow state this hook exists to remove.
     *
     * It is needed at all because a re-read that FAILS would otherwise leave the card showing
     * the OLD flag after a correction that landed — inviting the reviewer to correct it again
     * and append a second entry to a permanent audit trail.
     */
    const applyWrittenFlag = useCallback((encryptedFileUuid: string, identifiable: boolean) => {
        setVisit((prev) => (prev ? {
            ...prev,
            images: prev.images.map((image) => (image.encryptedFileUuid === encryptedFileUuid
                ? { ...image, identifiable }
                : image)),
        } : prev));
    }, []);

    /**
     * Approval, applied locally rather than by refetching: the server has just said the visit
     * is locked, and a reload would blank a screen the reviewer is still looking at. It is a
     * write to the payload like the folds above, and it lives here for the same reason —
     * there is one place that decides what a loaded visit currently says.
     */
    const markApproved = useCallback(() => {
        setVisit((prev) => (prev ? { ...prev, state: 'approved' } : prev));
    }, []);

    /**
     * SKIN-577 — the correction flag as the server recorded it after a step: a fold, like the
     * approval above. The step's own answer is the flag, so nothing is re-read.
     */
    const applyFlag = useCallback((flag: CorrectionFlagView) => {
        setVisit((prev) => (prev ? { ...prev, flag } : prev));
    }, []);

    return {
        visit,
        baseImages,
        corrections,
        assessments,
        lesionValues,
        undiagnosedLesions,
        workupByLesion,
        lesionCounts,
        assessmentWrites,
        lesionWrites,
        applyVisit,
        applyCorrection,
        applyWrittenFlag,
        markApproved,
        applyFlag,
    };
}
